import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function LoginPage() {
  const { tenant } = useParams<{ tenant?: string }>();
  const tenantSlug = tenant; // Direct tenant from URL: /[tenant]/login
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const { login, isLoading } = useAuth();
  const { t } = useTranslation();

  // Load company logo on component mount
  useEffect(() => {
    if (tenantSlug) {
      fetchCompanyLogo();
    }
  }, [tenantSlug]);

  const fetchCompanyLogo = async () => {
    try {
      const response = await fetch(`/api/tenants/${tenantSlug}/logo`);
      if (response.ok) {
        const data = await response.json();
        setCompanyLogo(data.logoUrl);
      }
    } catch (error) {
      console.log('No logo found for tenant:', tenantSlug);
      // Logo is optional, so we don't show an error
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !tenantSlug) return;
    
    setError('');
    try {
      await login(email, password, tenantSlug);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Link to="/" className="flex items-center justify-center mb-4">
            <img src="/qpulse-logo.png" alt="QPulseMES" className="w-12 h-12 object-contain" />
          </Link>
          
          {/* Company Logo */}
          {companyLogo && (
            <div className="mb-4">
              <img 
                src={companyLogo} 
                alt="Company Logo" 
                className="h-16 w-auto mx-auto object-contain rounded-lg shadow-sm"
              />
            </div>
          )}
          
          <CardTitle className="text-2xl">{t('auth.loginTitle')}</CardTitle>
          <CardDescription>
            Company: <span className="font-medium text-blue-600">{tenantSlug}</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="email"
                placeholder={t('common.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <Input
                type="password"
                placeholder={t('auth.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? t('common.loading') : t('auth.login')}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-sm text-blue-600 hover:underline"
            >
              Different company?
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}